import type {
  CommandAction,
  CommandPayload,
  CommandResult,
  DeviceId,
  Topic,
  TopicPayloads,
} from '@glyph/protocol-ts';

export type ClientKind = 'local' | 'cloud' | 'mock';

export interface CmdOptions {
  /** Override the per-action timeout from COMMAND_META. */
  timeoutMs?: number;
  /** Cloud only: allow the command to be queued if the device is offline (only for queueable actions). */
  queue?: boolean;
  signal?: AbortSignal;
}

export type TopicEvent<T extends Topic = Topic> = {
  [K in T]: { topic: K; deviceId: DeviceId; ts: number; data: TopicPayloads[K] };
}[T];

export type BinaryEvent =
  | { kind: 'preview'; deviceId: DeviceId; jpeg: Uint8Array; ts: number }
  | { kind: 'audio'; deviceId: DeviceId; seq: number; tsMs: number; opus: Uint8Array };

export type ClientEvent =
  | TopicEvent
  | { topic: '$binary'; deviceId: DeviceId; ts: number; data: BinaryEvent }
  | { topic: '$connection'; deviceId: DeviceId; ts: number; data: { connected: boolean } };

export type EventHandler = (ev: ClientEvent) => void;
export type Unsubscribe = () => void;

export type AudioSessionState = 'starting' | 'playing' | 'stalled' | 'stopped' | 'error';
export interface AudioSession {
  readonly deviceId: DeviceId;
  readonly state: AudioSessionState;
  /** Jitter-buffer depth the player is currently targeting. */
  readonly bufferMs: number;
  readonly level: number; // dBFS
  onFrame(cb: (frame: { seq: number; tsMs: number; opus: Uint8Array }) => void): Unsubscribe;
  onState(cb: (s: AudioSessionState) => void): Unsubscribe;
  stop(): Promise<void>;
}

export interface RestClient {
  get<T>(path: string, init?: RequestInit): Promise<T>;
  post<T>(path: string, body?: unknown, init?: RequestInit): Promise<T>;
  patch<T>(path: string, body?: unknown, init?: RequestInit): Promise<T>;
  put<T>(path: string, body?: unknown, init?: RequestInit): Promise<T>;
  del<T>(path: string, init?: RequestInit): Promise<T>;
  /** Absolute URL for a resource (download links, MJPEG <img src>). */
  url(path: string): string;
}

/**
 * The one typed client every hook consumes (docs/PLAN.md "Frontend"). Implemented by LocalClient,
 * CloudClient and MockClient. `cmd` resolves to the command's result model (the `data` of CmdResult).
 */
export interface CommandClient {
  readonly kind: ClientKind;
  /** Local shell always talks to "self"; cloud passes real device ids. */
  readonly selfDeviceId: DeviceId | null;
  cmd<A extends CommandAction>(
    deviceId: DeviceId,
    action: A,
    payload: CommandPayload<A>,
    opts?: CmdOptions,
  ): Promise<CommandResult<A>>;
  subscribe(deviceId: DeviceId, topics: readonly Topic[], onEvent: EventHandler): Unsubscribe;
  listen(deviceId: DeviceId): AudioSession;
  readonly rest: RestClient;
  /** URL of the live MJPEG preview for a device/output (null if not available for this client). */
  previewUrl(deviceId: DeviceId, output?: string): string | null;
  /** Re-open the event socket with the current credentials (after a PIN unlock / lock). Optional. */
  reconnect?(): void;
  /** Cloud: remember the CSRF token the API hands back with `me` / `login` (double-submit header). Optional. */
  setCsrf?(token: string | null): void;
  close(): void;
}

export type CommandErrorCode = 'timeout' | 'offline' | 'forbidden' | 'rejected' | 'network' | 'unknown';

export class CommandError extends Error {
  constructor(
    message: string,
    public readonly code: CommandErrorCode,
    public readonly status?: number,
    public readonly detail?: unknown,
  ) {
    super(message);
    this.name = 'CommandError';
  }
}

/** Map a CmdResult / HTTP status to an error code. */
export const codeForStatus = (status: number | undefined): CommandErrorCode =>
  status === 401 || status === 403
    ? 'forbidden'
    : status === 409 || status === 503
      ? 'offline'
      : status === 504 || status === 408
        ? 'timeout'
        : 'rejected';

/** Unwrap a `CmdResult` envelope: throw on !ok, return `data`. */
export function unwrapCmdResult<T>(
  action: string,
  r: { ok: boolean; data?: unknown; error?: string | null; status?: number },
): T {
  if (!r || typeof r.ok !== 'boolean') return r as unknown as T; // tolerant: already-unwrapped data
  if (!r.ok) throw new CommandError(r.error ?? `${action} failed`, codeForStatus(r.status), r.status, r);
  return (r.data ?? {}) as T;
}

export const newSessionId = (): string =>
  typeof crypto !== 'undefined' && 'randomUUID' in crypto
    ? crypto.randomUUID()
    : `s-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
